import React, { useEffect } from 'react';
import { Button, Col, Container, Row } from 'reactstrap';
import { Link } from 'gatsby';
import {
  toSignIn,
  toGetStarted,
  toBrowseDentalAssistantJobs,
  toBrowseDentalFrontOfficeJobs,
  toBrowseDentalHygienistJobs,
  toBrowseDentistJobs,
  toBrowseDentalLabTechJobs,
  toBrowseDentalSalesRepJobs,
  toAssessmentsMarketing,
  toEventsList,
  toStaffTempMarketingPage,
  toCorporateContact,
  toEmployerLearnMore,
  toResumeSearchMarketing,
  toCandidateMatchingMarketing,
  toClassifiedsList,
  toSalarySurvey,
  toJobseekerToolkit,
  toWhoKit,
  toDentalCareerGuide,
  toInstructorToolkit,
  toResources,
} from '../../routes';
import NavSlideover from './NavSlideover';
import Navbar from './Navbar';
import Footer from './Footer';
import PromoPrompt from '../promo/PromoPrompt';
import { PropsWithChildren } from 'react';
import { FaEnvelope, FaNewspaper, FaPuzzlePiece } from 'react-icons/fa';
import LocalImage from '../common/images/LocalImage';

export const MARKETING_NAV = [
  {
    title: 'Jobs',
    mainNav: {
      header: 'Find a Job',
      description: 'Browse jobs for the following positions near you.',
      links: [
        {
          label: 'Dental Assistants',
          route: toBrowseDentalAssistantJobs,
          externalInCapacitor: true,
        },
        {
          label: 'Dental Front Office',
          route: toBrowseDentalFrontOfficeJobs,
          externalInCapacitor: true,
        },
        {
          label: 'Dental Hygienists',
          route: toBrowseDentalHygienistJobs,
          externalInCapacitor: true,
        },
        {
          label: 'Dentists',
          route: toBrowseDentistJobs,
          externalInCapacitor: true,
        },
        {
          label: 'Dental Lab Technicians',
          route: toBrowseDentalLabTechJobs,
          externalInCapacitor: true,
        },
        {
          label: 'Dental Sales Rep',
          route: toBrowseDentalSalesRepJobs,
          externalInCapacitor: true,
        },
      ],
      cta: {
        label: 'Browse all dental jobs',
        route: '/dental-jobs/',
      },
    },
    featureNav: {
      header: 'Dental Professional Features',
      links: [
        {
          icon: (
            <LocalImage
              src="homepage/pie-chart.png"
              alt="pie chart"
              width={35}
              height={35}
              style={{ pointerEvents: 'none' }}
            />
          ),
          header: 'Assessments',
          description:
            'Understand yourself better so you can make the right career moves. Assessments are always FREE for job seekers.',
          route: toAssessmentsMarketing,
        },
        {
          icon: (
            <LocalImage
              src="ce-icon.png"
              alt="open book"
              width={35}
              height={35}
              style={{ pointerEvents: 'none' }}
            />
          ),
          header: 'Continuing Education (CE)',
          description:
            'Track & maintain CE credits right on your DentalPost dashboard, and get special course pricing and offers.',
          route: toEventsList,
        },
        {
          icon: (
            <LocalImage
              src="temp-icon.png"
              alt="searching for temp work"
              width={35}
              height={35}
              style={{ pointerEvents: 'none' }}
            />
          ),
          header: 'Temp Jobs',
          description:
            "Open to temp? Choose the days you're available to work and get alerts when practices need your help. Control your schedule, make extra money.",
          route: toStaffTempMarketingPage,
        },
      ],
    },
  },

  {
    title: 'Resources',
    mainNav: {
      header: 'Resources',
      description:
        'Access the data, guidance and information you need to be the most successful in your dental career.',
      links: [
        {
          label: '2026 Salary Survey Report',
          route: toSalarySurvey,
        },
        {
          label: 'Industry Events',
          route: toEventsList,
        },
        {
          label: 'Jobseeker Toolkit',
          route: toJobseekerToolkit,
        },
        {
          label: 'Hiring Toolkit',
          route: toWhoKit,
        },
        {
          label: 'Dental Career Guide',
          route: toDentalCareerGuide,
        },
        {
          label: 'Educator Toolkit',
          route: toInstructorToolkit,
        },
        {
          label: 'Dental Marketing',
          href: 'https://www.amplify360.com/',
        },
      ],
      cta: {
        label: 'View all resources',
        route: toResources,
      },
    },
    blogNav: true,
  },
  {
    title: 'Employers',
    mainNav: {
      header: 'Post a Job',
      description: 'Find and Hire Qualified Dental Professionals',
      links: [
        {
          label: 'Premium Job Posting Subscription',
          route: toEmployerLearnMore,
        },
        {
          label: 'Corporate Services',
          href: toCorporateContact,
        },
      ],
      cta: {
        label: 'View all products & pricing',
        route: toEmployerLearnMore,
      },
    },
    featureNav: {
      header: 'Dental Employer Features',
      links: [
        {
          icon: (
            <FaEnvelope
              className="text-secondary"
              style={{ fontSize: '35px' }}
            />
          ),
          header: 'Resume Search - Direct Messaging',
          description:
            'Research public candidates in your area and start direct conversations before they apply to your job',
          route: toResumeSearchMarketing,
        },
        {
          icon: (
            <FaPuzzlePiece
              className="text-success"
              style={{ fontSize: '35px' }}
            />
          ),
          header: 'Candidate Matching',
          description:
            'Save time and money with DentalPost’s proprietary matching algorithm.',
          route: toCandidateMatchingMarketing,
        },
        {
          icon: (
            <FaNewspaper className="text-info" style={{ fontSize: '35px' }} />
          ),
          header: 'Dental Classifieds',
          description:
            'Our dental classified ads connect buyers and sellers locally and around the world.',
          route: toClassifiedsList(),
        },
      ],
    },
  },
];

const UnauthenticatedLayout = (props: PropsWithChildren) => {
  useEffect(() => {
    const header = document.getElementById('unauth-header');
    if (!header) return;
    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        '--dp-header-height',
        `${entry.contentRect.height}px`
      );
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <Container fluid className="bg-white px-0">
      <PromoPrompt mode="banner" />
      <Row
        id="unauth-header"
        className="g-0 border-bottom border-light w-100"
        style={{ position: 'sticky', top: 0, zIndex: 1000 }}
      >
        <Col className="px-0">
          <Navbar>
            {/* DESKTOP ACTIONS */}
            <div className="d-none d-lg-flex">
              <Button
                color="link"
                className="text-black fw-light me-2"
                tag={Link}
                to={toSignIn}
              >
                Sign In
              </Button>
              <Button color="primary" outline tag={Link} to={toGetStarted}>
                Create Account
              </Button>
            </div>
            {/* MOBILE ACTIONS */}
            <NavSlideover />
          </Navbar>
        </Col>
      </Row>
      <Row
        className={'g-0 position-relative not-authenticated flex-grow-1'}
        style={{ overflowX: 'clip' }}
        id="main-content"
      >
        <Col className="px-0">
          <main role="main">{props.children}</main>
          <Footer />
        </Col>
      </Row>
    </Container>
  );
};

export default UnauthenticatedLayout;
